import { useEffect } from 'react'

const KEYBOARD_OPEN_THRESHOLD_PX = 80
const FOCUS_SCROLL_DELAY_MS = 300
const FOCUS_SCROLL_GAP_PX = 16

function isEditable(element: Element | null): element is HTMLElement {
  return (
    element instanceof HTMLElement &&
    element.matches(
      'input, textarea, select, [contenteditable="true"]',
    )
  )
}

/**
 * Keeps the visual keyboard from covering form inputs without moving the
 * bottom navigation. The layout viewport stays full-height (mobile browsers
 * use `interactive-widget=resizes-visual`), the visual viewport shrinks, and
 * this hook publishes the overlap as `--keyboard-inset`.
 *
 * Only the main content and the chat composer consume that variable. The
 * fixed bottom navigation deliberately ignores it and remains behind the
 * keyboard, as requested.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return

    const root = document.documentElement
    const viewport = window.visualViewport
    let frame = 0
    let focusTimer = 0

    const update = () => {
      frame = 0
      // The app uses a fixed layout viewport (resizes-visual), so the overlap
      // is the difference in height. Do not subtract viewport.offsetTop here:
      // iOS pans the visual viewport while focusing a field, and feeding that
      // offset back into the page height makes the composer bounce.
      const inset = Math.max(
        0,
        Math.round(window.innerHeight - viewport.height),
      )
      root.style.setProperty('--keyboard-inset', `${inset}px`)
      root.classList.toggle(
        'keyboard-open',
        inset > KEYBOARD_OPEN_THRESHOLD_PX,
      )
    }

    const schedule = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(update)
    }

    const scrollFocusedIntoView = () => {
      const active = document.activeElement
      if (!isEditable(active)) return
      // A sticky composer already tracks the keyboard. Scrolling the window
      // here would move the chat header and make the composer jump.
      if (active.closest('[data-keyboard-sticky]')) return
      if (
        !root.classList.contains('keyboard-open') ||
        (Number.parseFloat(
          getComputedStyle(root).getPropertyValue('--keyboard-inset'),
        ) || 0) <= 0
      ) {
        return
      }

      const rect = active.getBoundingClientRect()
      const overflow = rect.bottom - (viewport.height - FOCUS_SCROLL_GAP_PX)
      if (overflow > 0) {
        window.scrollBy({ top: overflow, behavior: 'smooth' })
      }
    }

    const onFocusIn = (event: FocusEvent) => {
      if (!isEditable(event.target as Element | null)) return
      window.clearTimeout(focusTimer)
      focusTimer = window.setTimeout(
        scrollFocusedIntoView,
        FOCUS_SCROLL_DELAY_MS,
      )
    }

    const onOrientationChange = () => {
      // The keyboard usually closes on rotation; publish the settled value.
      schedule()
    }

    update()
    viewport.addEventListener('resize', schedule)
    viewport.addEventListener('scroll', schedule)
    window.addEventListener('focusin', onFocusIn)
    window.addEventListener('orientationchange', onOrientationChange)

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame)
      window.clearTimeout(focusTimer)
      viewport.removeEventListener('resize', schedule)
      viewport.removeEventListener('scroll', schedule)
      window.removeEventListener('focusin', onFocusIn)
      window.removeEventListener('orientationchange', onOrientationChange)
      root.style.removeProperty('--keyboard-inset')
      root.classList.remove('keyboard-open')
    }
  }, [])
}
